import React, { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { SearchCursor } from '@codemirror/search';
import { Compartment, EditorState, RangeSetBuilder, StateEffect, StateField } from '@codemirror/state';
import { EditorView, Decoration, lineNumbers } from '@codemirror/view';
import { HighlightStyle, StreamLanguage, syntaxHighlighting } from '@codemirror/language';
import { diff as diffMode } from '@codemirror/legacy-modes/mode/diff';
import { tags as t } from '@lezer/highlight';
import { javascript as jsonLanguage } from '@codemirror/lang-javascript';
import { arista } from './configLanguages/aristaLanguage';
import { junos } from './configLanguages/junosLanguage';
import { fortigate } from './configLanguages/fortigateLanguage';

const JUNOS_EXTENSION = junos().extension;
const ARISTA_EXTENSION = arista().extension;
const FORTIGATE_EXTENSION = fortigate().extension;
const DIFF_EXTENSION = StreamLanguage.define(diffMode);
const JSON_EXTENSION = jsonLanguage();

const palette = {
  atom: '#F78C6C',
  attribute: '#C792EA',
  builtin: '#FFCB6B',
  comment: '#546E7A',
  def: '#82AAFF',
  keyword: '#C792EA',
  meta: '#FFCB6B',
  number: '#FF5370',
  operator: '#89DDFF',
  property: '#C792EA',
  qualifier: '#DECB6B',
  string: '#C3E88D',
  string2: '#F07178',
  tag: '#FF5370',
  type: '#F07178',
  variable: '#F07178',
  variable2: '#EEFFFF',
  variable3: '#DECB6B',
  matchBg: '#FFEB3B',
  activeMatchBg: '#FF9800',
  activeMatchOutline: '#FF5722',
  whiteTxt: '#000000'
};

const deviceConfigEditorTheme = EditorView.theme(
  {
    '&': {
      backgroundColor: '#263238',
      color: '#CAD3F5',
      fontFamily: "'roboto mono', 'menlo', 'monaco', monospace",
      fontSize: '14px'
    },
    '.cm-content': {
      padding: '16px'
    },
    '.cm-scroller': {
      lineHeight: '1.5'
    },
    '.cm-gutters': {
      backgroundColor: '#263238',
      color: '#6E738D',
      border: 'none'
    },
    '&.cm-editor.cm-focused': {
      outline: 'none'
    },
    '.cm-search-match': {
      backgroundColor: palette.matchBg,
      fontWeight: 'bold'
    },
    '.cm-search-match-active': {
      backgroundColor: palette.activeMatchBg,
      fontWeight: 'bold',
      outline: `2px solid ${palette.activeMatchOutline}`
    }
  },
  { dark: true }
);

const unifiedHighlightStyle = HighlightStyle.define([
  { tag: [t.keyword, t.definitionKeyword], color: palette.variable3, fontWeight: 600 },
  { tag: t.controlKeyword, color: palette.meta, fontWeight: 600 },
  { tag: t.propertyName, color: palette.property },
  { tag: t.variableName, color: palette.variable2 },
  { tag: [t.number, t.unit], color: palette.number },
  { tag: t.atom, color: palette.atom },
  { tag: t.bool, color: palette.atom },
  { tag: t.string, color: palette.string },
  { tag: t.lineComment, color: palette.comment, fontStyle: 'italic' },
  { tag: [t.separator, t.brace, t.bracket], color: palette.comment },
  { tag: t.inserted, background: 'rgba(76,175,80,0.18)', color: '#A5D6A7' },
  { tag: t.deleted, background: 'rgba(244,67,54,0.22)', color: '#FFAB91' },
  { tag: t.meta, color: '#90CAF9', fontWeight: 'bold' },
  { tag: t.comment, color: '#FFE082' }
]);

const searchMatchDecoration = Decoration.mark({ class: 'cm-search-match' });
const activeSearchMatchDecoration = Decoration.mark({ class: 'cm-search-match-active' });

const setSearchMatchEffect = StateEffect.define();

const searchMatchField = StateField.define({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    // If there's a setSearchMatchEffect, replace decorations with the new set
    for (const effect of tr.effects) {
      if (effect.is(setSearchMatchEffect)) {
        return effect.value;
      }
    }
    // Otherwise, map decorations through document changes
    return decorations.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f)
});

const BASE_EDITOR_EXTENSIONS = [
  lineNumbers(),
  EditorView.lineWrapping,
  EditorState.tabSize.of(2),
  EditorState.readOnly.of(true),
  EditorView.editable.of(false),
  deviceConfigEditorTheme,
  syntaxHighlighting(unifiedHighlightStyle)
];

const getLanguageKey = (syntax) => {
  const value = typeof syntax === 'string' ? syntax.toLowerCase() : '';
  if (value === 'diff') {
    return 'diff';
  }
  if (value.includes('arista') || value.includes('cisco')) {
    return 'arista';
  }
  if (value.includes('juniper') || value.includes('junos')) {
    return 'junos';
  }
  if (value.includes('nokia')) {
    return 'json';
  }
  if (value.includes('fortinet')) {
    return 'fortigate';
  }
  return 'plain';
};

const getLanguageExtensionForKey = (key) => {
  if (key === 'junos') {
    return JUNOS_EXTENSION;
  }
  if (key === 'arista') {
    return ARISTA_EXTENSION;
  }
  if (key === 'diff') {
    return DIFF_EXTENSION;
  }
  if (key === 'json') {
    return JSON_EXTENSION;
  }
  if (key === 'fortigate') {
    return FORTIGATE_EXTENSION;
  }
  return [];
};

export const isDeviceConfigLanguage = (syntax) => {
  const key = getLanguageKey(syntax);
  const extension = getLanguageExtensionForKey(key);
  return Array.isArray(extension) ? extension.length > 0 : !!extension;
};

const DeviceConfigView = forwardRef(({ configText, syntax, className, searchQuery = '', onSearchMatchChange }, ref) => {
  const containerRef = useRef(null);
  const editorViewRef = useRef(null);
  const languageCompartmentRef = useRef(new Compartment());
  const currentLanguageKeyRef = useRef(null);
  const languageKey = getLanguageKey(syntax);
  const searchMatchesRef = useRef([]);
  const currentMatchIndexRef = useRef(0);

  const getMatchInfo = useCallback(
    () => ({
      totalMatches: searchMatchesRef.current.length,
      currentMatchIndex: searchMatchesRef.current.length > 0 ? currentMatchIndexRef.current : -1
    }),
    []
  );

  const findNextMatch = useCallback(() => {
    const view = editorViewRef.current;
    if (!view || !searchQuery || searchMatchesRef.current.length === 0) {
      return getMatchInfo();
    }

    // Move to next match (wrap around to 0 if at the end)
    currentMatchIndexRef.current = (currentMatchIndexRef.current + 1) % searchMatchesRef.current.length;
    const currentMatch = searchMatchesRef.current[currentMatchIndexRef.current];

    // Rebuild decorations with the current match highlighted differently
    const builder = new RangeSetBuilder();
    searchMatchesRef.current.forEach((match, index) => {
      const decoration = index === currentMatchIndexRef.current ? activeSearchMatchDecoration : searchMatchDecoration;
      builder.add(match.from, match.to, decoration);
    });

    // Update decorations and scroll to current match
    view.dispatch({
      effects: setSearchMatchEffect.of(builder.finish()),
      selection: { anchor: currentMatch.from },
      scrollIntoView: true
    });

    return getMatchInfo();
  }, [searchQuery, getMatchInfo]);

  const findPreviousMatch = useCallback(() => {
    const view = editorViewRef.current;
    if (!view || !searchQuery || searchMatchesRef.current.length === 0) {
      return getMatchInfo();
    }

    // Move to previous match (wrap around to end if at the beginning)
    currentMatchIndexRef.current =
      (currentMatchIndexRef.current - 1 + searchMatchesRef.current.length) % searchMatchesRef.current.length;
    const currentMatch = searchMatchesRef.current[currentMatchIndexRef.current];

    // Rebuild decorations with the current match highlighted differently
    const builder = new RangeSetBuilder();
    searchMatchesRef.current.forEach((match, index) => {
      const decoration = index === currentMatchIndexRef.current ? activeSearchMatchDecoration : searchMatchDecoration;
      builder.add(match.from, match.to, decoration);
    });

    // Update decorations and scroll to current match
    view.dispatch({
      effects: setSearchMatchEffect.of(builder.finish()),
      selection: { anchor: currentMatch.from },
      scrollIntoView: true
    });

    return getMatchInfo();
  }, [searchQuery, getMatchInfo]);

  // Expose findNextMatch, findPreviousMatch, and getMatchInfo to parent component
  useImperativeHandle(
    ref,
    () => ({
      findNextMatch,
      findPreviousMatch,
      getMatchInfo
    }),
    [findNextMatch, findPreviousMatch, getMatchInfo]
  );

  useEffect(() => {
    if (!containerRef.current || editorViewRef.current) {
      return;
    }

    const languageExtension = getLanguageExtensionForKey(languageKey);

    const state = EditorState.create({
      doc: typeof configText === 'string' ? configText : '',
      extensions: [...BASE_EDITOR_EXTENSIONS, languageCompartmentRef.current.of(languageExtension), searchMatchField]
    });

    containerRef.current.innerHTML = '';
    editorViewRef.current = new EditorView({
      state,
      parent: containerRef.current
    });
    currentLanguageKeyRef.current = languageKey;
  }, [configText, languageKey]);

  useEffect(
    () => () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy();
        editorViewRef.current = null;
      }
      currentLanguageKeyRef.current = null;
    },
    []
  );

  useEffect(() => {
    const view = editorViewRef.current;
    if (!view) {
      return;
    }

    const next = typeof configText === 'string' ? configText : '';
    const current = view.state.doc.toString();

    if (next === current) {
      return;
    }

    view.dispatch({
      changes: { from: 0, to: current.length, insert: next }
    });
  }, [configText]);

  useEffect(() => {
    const view = editorViewRef.current;
    if (!view) {
      return;
    }

    // If no search query, clear decorations and matches
    if (!searchQuery) {
      searchMatchesRef.current = [];
      currentMatchIndexRef.current = 0;
      view.dispatch({
        effects: setSearchMatchEffect.of(Decoration.none)
      });
      // Notify parent about cleared matches
      if (onSearchMatchChange) {
        onSearchMatchChange({ totalMatches: 0, currentMatchIndex: -1 });
      }
      return;
    }

    // Create case-insensitive search cursor
    const cursor = new SearchCursor(view.state.doc, searchQuery, 0, undefined, (x) => x.toLowerCase());
    const builder = new RangeSetBuilder();
    const matches = [];

    // Iterate through all matches and add decorations
    while (!cursor.next().done) {
      const { from, to } = cursor.value;
      matches.push({ from, to });
      // First match is active, others are regular
      const decoration = matches.length === 1 ? activeSearchMatchDecoration : searchMatchDecoration;
      builder.add(from, to, decoration);
    }

    // Store matches and reset current index
    searchMatchesRef.current = matches;
    currentMatchIndexRef.current = 0;

    // Dispatch the effect to update the decorations
    view.dispatch({
      effects: setSearchMatchEffect.of(builder.finish())
    });

    // Scroll to the first match if found
    if (matches.length > 0) {
      view.dispatch({
        selection: { anchor: matches[0].from },
        scrollIntoView: true
      });
    }

    // Notify parent about new matches
    if (onSearchMatchChange) {
      onSearchMatchChange({
        totalMatches: matches.length,
        currentMatchIndex: matches.length > 0 ? 0 : -1
      });
    }
  }, [configText, searchQuery, onSearchMatchChange]);

  useEffect(() => {
    const view = editorViewRef.current;
    if (!view) {
      return;
    }
    if (currentLanguageKeyRef.current === languageKey) {
      return;
    }

    currentLanguageKeyRef.current = languageKey;
    const languageExtension = getLanguageExtensionForKey(languageKey);
    view.dispatch({ effects: languageCompartmentRef.current.reconfigure(languageExtension) });
  }, [languageKey]);

  return <div ref={containerRef} className={className} />;
});

DeviceConfigView.displayName = 'DeviceConfigView';

DeviceConfigView.propTypes = {
  className: PropTypes.string,
  configText: PropTypes.string,
  syntax: PropTypes.string,
  searchQuery: PropTypes.string,
  onSearchMatchChange: PropTypes.func
};

DeviceConfigView.defaultProps = {
  className: undefined,
  configText: '',
  syntax: '',
  searchQuery: '',
  onSearchMatchChange: undefined
};

export default DeviceConfigView;
